import { ApiClientError, buildApiUrl, getApiErrorMessage, parseJson } from '@/lib/api/client';
import type { ApiErrorResponse } from '@/lib/auth/types';

const DEFAULT_SUCCESS_MESSAGE = 'Cita creada correctamente.';
const DEFAULT_NETWORK_ERROR_MESSAGE = 'No pudimos crear la cita. Intenta nuevamente.';

interface CreateProfessionalAppointmentResponse extends ApiErrorResponse {
  appointment?: {
    id?: string;
  };
  client?: {
    id?: string;
  };
  ok?: boolean;
  professional?: {
    id?: string;
  };
  service?: {
    id?: string;
  };
}

function getCreateProfessionalAppointmentEndpoint(): string {
  const configuredEndpoint = process.env.NEXT_PUBLIC_CREATE_PROFESSIONAL_APPOINTMENT_ENDPOINT?.trim();

  if (configuredEndpoint) {
    return buildApiUrl(configuredEndpoint);
  }

  const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL?.trim();

  if (supabaseUrl) {
    return `${supabaseUrl.replace(/\/+$/, '')}/functions/v1/create-professional-appointment`;
  }

  throw new ApiClientError(
    'Configura NEXT_PUBLIC_CREATE_PROFESSIONAL_APPOINTMENT_ENDPOINT o NEXT_PUBLIC_SUPABASE_URL para crear citas.'
  );
}

export async function createProfessionalAppointment({
  authToken,
  appointmentOtpChallengeId,
  businessId,
  clientName,
  clientPhone,
  clientUserId,
  notes,
  professionalId,
  publicBooking = false,
  reservadoVia,
  serviceTypeId,
  startAt,
  unregisteredClient = false,
}: {
  appointmentOtpChallengeId?: string;
  authToken?: string;
  businessId?: string;
  clientName?: string;
  clientPhone?: string;
  clientUserId?: string;
  notes?: string;
  professionalId?: string;
  publicBooking?: boolean;
  reservadoVia?: 'PAGINA';
  serviceTypeId: string;
  startAt: string;
  unregisteredClient?: boolean;
}) {
  try {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    if (authToken) {
      headers.Authorization = `Bearer ${authToken}`;
    }

    const supabaseAnonKey = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY?.trim();

    if (supabaseAnonKey) {
      headers.apikey = supabaseAnonKey;
      if (!headers.Authorization) {
        headers.Authorization = `Bearer ${supabaseAnonKey}`;
      }
    }

    const response = await fetch(getCreateProfessionalAppointmentEndpoint(), {
      method: 'POST',
      headers,
      body: JSON.stringify(
        publicBooking
          ? {
              appointment_otp_challenge_id: appointmentOtpChallengeId,
              business_id: businessId,
              client_name: clientName?.trim() ? clientName.trim() : undefined,
              client_phone: clientPhone,
              notes: notes?.trim() ? notes.trim() : undefined,
              professional_id: professionalId,
              public_booking: true,
              reservado_via: reservadoVia,
              service_type_id: serviceTypeId,
              start_at: startAt,
            }
          : {
              client_name: unregisteredClient && clientName?.trim() ? clientName.trim() : undefined,
              client_user_id: unregisteredClient ? undefined : clientUserId,
              notes: notes?.trim() ? notes.trim() : undefined,
              reservado_via: reservadoVia,
              service_type_id: serviceTypeId,
              start_at: startAt,
              unregistered_client: unregisteredClient || undefined,
            }
      ),
    });

    const payload = await parseJson<CreateProfessionalAppointmentResponse>(response);

    if (!response.ok || payload?.ok === false) {
      const message =
        getApiErrorMessage(payload) ??
        (response.status >= 500
          ? 'No fue posible crear la cita en este momento.'
          : 'No pudimos crear la cita con los datos enviados.');

      throw new ApiClientError(message, response.status);
    }

    return {
      appointmentId: payload?.appointment?.id ?? null,
      clientId: payload?.client?.id ?? null,
      message: getApiErrorMessage(payload) ?? DEFAULT_SUCCESS_MESSAGE,
      professionalId: payload?.professional?.id ?? null,
      serviceId: payload?.service?.id ?? null,
    };
  } catch (error) {
    if (error instanceof ApiClientError) {
      throw error;
    }

    throw new ApiClientError(DEFAULT_NETWORK_ERROR_MESSAGE);
  }
}
